import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { toast } from 'react-toastify';
import { skillstreeApi } from '../services/api';
import { subscriptionService } from '../services/subscriptionService';
import { dashboardService } from '@/services/dashboardService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import './SkillstreeMicroskillPage.css';

interface Microskill {
  id: number | null;
  code: string;
  name: string;
  title: string;
  sessions: number;
  hours: number;
  canAccess?: boolean;
  is_free?: boolean;
  learning_status?: string;
  learning_progress?: number;
  videos?: Array<{
    video?: {
      duration?: number;
    };
    thumbnail?: {
      url?: string;
    };
  }>;
}

interface SkillstreeDetail {
  id: number;
  name: string;
  description: string;
  image_url?: string;
  monthlyCompensation: string;
  demand: string;
  microskills: Microskill[];
  is_enrolled?: boolean;
  learning_progress?: number;
  can_enroll?: boolean;
  is_completed?: boolean;
}

interface RelatedSkillstree {
  id: number;
  name: string;
  description: string;
  image_url?: string;
}

const SkillstreeMicroskillPage = () => {
  const { categoryId, skillstreeId } = useParams<{ categoryId: string; skillstreeId: string }>();
  const navigate = useNavigate();
  
  const [skillstree, setSkillstree] = useState<SkillstreeDetail | null>(null);
  const [categoryTitle, setCategoryTitle] = useState<string>('');
  const [categorySlug, setCategorySlug] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [relatedSkillstrees, setRelatedSkillstrees] = useState<RelatedSkillstree[]>([]);
  const [enrolling, setEnrolling] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [hasFreeSubscription, setHasFreeSubscription] = useState<boolean>(false);
  const [isFreeTrial, setIsFreeTrial] = useState<boolean>(false);
  const [activeEnrollments, setActiveEnrollments] = useState<number>(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Scroll to top when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId, skillstreeId]);

  // Fetch skillstree data from API
  useEffect(() => {
    const fetchSkillstree = async () => {
      if (!categoryId || !skillstreeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch category slug internally (same as CategoryDetailPage)
        const categoriesResponse = await skillstreeApi.getCoreCategories();
        const categoriesData = (categoriesResponse.data as any).data;
        
        if (categoriesData && Array.isArray(categoriesData)) {
          const categoryIdNum = parseInt(categoryId, 10);
          const category = categoriesData.find((cat: any) => cat.id === categoryIdNum);
          if (category && category.slug) {
            setCategorySlug(category.slug);
          }
        }
        
        // Call the API endpoint
        const response = await skillstreeApi.getSkillstreeById(skillstreeId);
        const apiData = (response.data as any).data; // API returns { status, data: {...}, message }

        if (!apiData) {
          throw new Error('No data received from API');
        }

        // Get the first version's microskills (or combine all versions)
        const firstVersion = apiData.versions && apiData.versions.length > 0 ? apiData.versions[0] : null;
        
        if (!firstVersion || !firstVersion.micro_skills) {
          throw new Error('No microskills found');
        }

        // Map API microskills to component structure
        const microskills: Microskill[] = firstVersion.micro_skills.map((ms: any, index: number) => {
          // Calculate sessions and hours from videos
          const videoCount = ms.videos ? ms.videos.length : 0;
          const totalDurationSeconds = ms.videos 
            ? ms.videos.reduce((sum: number, video: any) => sum + (video.video?.duration || 0), 0)
            : 0;
          const totalHours = Math.round(totalDurationSeconds / 3600 * 10) / 10; // Round to 1 decimal

          // Only use valid IDs, don't fallback to index
          const validId = ms.micro_skill_id || ms.micro_skill_version_id || null;

          return {
            id: validId,
            code: ms.unique_code || '',
            name: ms.title || 'Unnamed Microskill',
            title: ms.title || '',
            sessions: videoCount,
            hours: totalHours || 0,
            canAccess: ms.can_access || false,
            is_free: ms.is_free !== undefined ? ms.is_free : true,
            learning_status: ms.learning_status,
            learning_progress: ms.learning_progress,
            videos: ms.videos || [],
          };
        });

        // Create skillstree detail object
        const skillstreeData: SkillstreeDetail = {
          id: apiData.skillstree_id,
          name: apiData.skillstree_name,
          description: apiData.description || '',
          image_url: apiData.image_url,
          monthlyCompensation: 'RM 3,750 to RM 7,917', // Not in API, using placeholder
          demand: 'High demand in Penang, especially in 2024', // Not in API, using placeholder
          microskills: microskills,
          can_enroll: apiData.can_enroll,
          is_enrolled: apiData.is_enrolled,
          learning_progress: firstVersion?.learning_progress || 0, // Get from initial API response as fallback
        };

        // Fetch enrollment status and completion status from /v1/skillstrees API
        try {
          const skillstreesResponse = await skillstreeApi.getSkillstrees();
          const skillstreesData = (skillstreesResponse.data as any).data || [];
          
          // Find the enrollment status for this skillstree
          const skillstreeEnrollment = skillstreesData.find((st: any) => st.skillstree_id === apiData.skillstree_id);
          if (skillstreeEnrollment) {
            skillstreeData.is_enrolled = skillstreeEnrollment.is_enrolled || false;
            // Override with learning_progress from /v1/skillstrees if available (more up-to-date)
            if (skillstreeEnrollment.versions?.[0]?.learning_progress !== undefined) {
              skillstreeData.learning_progress = skillstreeEnrollment.versions[0].learning_progress;
            }
            // Check completion status: either is_completed flag or learning_progress === 100
            const learningProgress = skillstreeEnrollment.versions?.[0]?.learning_progress || skillstreeData.learning_progress || 0;
            skillstreeData.is_completed = skillstreeEnrollment.is_completed === true || learningProgress === 100;
          }
        } catch (enrollmentErr) {
          console.error('Error fetching enrollment status:', enrollmentErr);
          // Continue without enrollment status if API fails, learning_progress already set from initial response
          // Set completion status based on learning_progress if available
          if (skillstreeData.learning_progress === 100) {
            skillstreeData.is_completed = true;
          }
        }

        setSkillstree(skillstreeData);
        setCategoryTitle(apiData.core_category_name || 'Category');
      } catch (err) {
        console.error('Error fetching skillstree:', err);
        setSkillstree(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSkillstree();
  }, [categoryId, skillstreeId]);

  // Fetch related skillstrees from API
  useEffect(() => {
    const fetchRelatedSkillstrees = async () => {
      // Only fetch if we have the category slug and current skillstree name
      if (!categorySlug || !skillstree?.name) {
        return;
      }

      try {
        // Fetch category data to get all skillstrees
        const response = await skillstreeApi.getCoreCategoryWithSkills(categorySlug);
        const apiData = (response.data as any).data; // API returns { status, data: {...}, message }

        if (!apiData || !apiData.skillstrees || !Array.isArray(apiData.skillstrees)) {
          return;
        }

        // Filter out the current skillstree and map to RelatedSkillstree format
        const related: RelatedSkillstree[] = apiData.skillstrees
          .filter((st: any) => st.skillstree_name !== skillstree.name)
          .map((st: any) => ({
            id: st.skillstree_id,
            name: st.skillstree_name || '',
            description: st.description || '',
            image_url: st.image_url || undefined,
          }));

        setRelatedSkillstrees(related);
      } catch (err) {
        console.error('Error fetching related skillstrees:', err);
        // Don't set error state, just leave relatedSkillstrees empty
      }
    };

    fetchRelatedSkillstrees();
  }, [categorySlug, skillstree?.name]);

  // Reinitialize carousel when related skillstrees change
  useEffect(() => {
    if (emblaApi && relatedSkillstrees.length > 0) {
      emblaApi.reInit();
    }
  }, [emblaApi, relatedSkillstrees]);

  // Check subscription status and active enrollments from profile API
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        // Fetch user profile from API
        const profileResponse = await dashboardService.getUserProfile();
        const profileData = (profileResponse?.data as any)?.data as any;
        const subscription = profileData?.subscription;
        
        // Check if subscription exists and is not expired
        let hasActiveSubscription = false;
        if (subscription) {
          // Check if subscription has expire_date and if it's in the future
          if (subscription.expire_date) {
            const expireDate = new Date(subscription.expire_date);
            const now = new Date();
            hasActiveSubscription = expireDate > now;
          } else if (subscription.is_active !== undefined) {
            // Use is_active flag if available
            hasActiveSubscription = subscription.is_active === true;
          } else {
            // Fallback: if subscription exists but no expiration/active info, assume active
            hasActiveSubscription = true;
          }
        }
        
        setHasSubscription(hasActiveSubscription);

        // Check if user has free trial subscription from profile API
        const isFreeTrialSubscription = subscription?.is_free_trial === true;
        setIsFreeTrial(isFreeTrialSubscription);
        
        // Also set hasFreeSubscription for backward compatibility
        setHasFreeSubscription(isFreeTrialSubscription);

        // Calculate active (non-completed) enrollments from profile API
        const profileSkillstrees = profileData?.skillstrees;
        const enrolledSkillstrees = profileSkillstrees?.enrolled || [];
        const activeEnrollmentsCount = enrolledSkillstrees.filter((st: any) => !st.is_completed).length;
        setActiveEnrollments(activeEnrollmentsCount);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setHasSubscription(false);
        setHasFreeSubscription(false);
        setIsFreeTrial(false);
        setActiveEnrollments(0);
      }
    };

    checkSubscriptionStatus();
  }, []);

  // Handle enroll button click - show confirmation dialog or redirect to subscription
  const handleEnroll = () => {
    if (!skillstree) return;
    
    // If user has free trial subscription, redirect to subscription page immediately
    // without showing confirmation dialog
    if (isFreeTrial) {
      navigate('/profile?tab=subscription');
      return;
    }
    
    // Otherwise, show confirmation dialog
    setConfirmDialogOpen(true);
  };

  // Confirm enrollment after user confirms
  const confirmEnroll = async () => {
    if (!skillstree) return;

    try {
      setEnrolling(true);
      setConfirmDialogOpen(false);
      await skillstreeApi.enroll(skillstree.id);
      
      // Fetch updated enrollment status from /v1/skillstrees API
      try {
        const skillstreesResponse = await skillstreeApi.getSkillstrees();
        const skillstreesData = (skillstreesResponse.data as any).data || [];
        
        // Find the enrollment status for this skillstree
        const skillstreeEnrollment = skillstreesData.find((st: any) => st.skillstree_id === skillstree.id);
        if (skillstreeEnrollment) {
          const learningProgress = skillstreeEnrollment.versions?.[0]?.learning_progress || 0;
          const isCompleted = skillstreeEnrollment.is_completed === true || learningProgress === 100;
          setSkillstree(prev => prev ? {
            ...prev,
            is_enrolled: skillstreeEnrollment.is_enrolled || false,
            can_enroll: skillstreeEnrollment.can_enroll || true,
            learning_progress: learningProgress,
            is_completed: isCompleted
          } : null);
        } else {
          // Fallback: update local state if API doesn't return the skillstree
          setSkillstree(prev => prev ? {
            ...prev,
            is_enrolled: true,
            can_enroll: false
          } : null);
        }
      } catch (enrollmentErr) {
        console.error('Error fetching updated enrollment status:', enrollmentErr);
        // Fallback: update local state if API fails
        setSkillstree(prev => prev ? {
          ...prev,
          is_enrolled: true,
          can_enroll: false,
          learning_progress: prev.learning_progress || 0,
          is_completed: prev.learning_progress === 100
        } : null);
      }
      
      toast.success('Successfully enrolled in skillstree!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err: any) {
      console.error('Error enrolling in skillstree:', err);
      console.error('Error response:', err?.response?.data);
      
      // Extract error message from various possible locations
      const errorMessage = 
        err?.response?.data?.message || 
        err?.response?.data?.error || 
        err?.message || 
        'Failed to enroll. Please try again.';
      
      // Check if user is already enrolled
      if (errorMessage.includes('You are already enrolled in this Skillstree')) {
        toast.info(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } finally {
      setEnrolling(false);
    }
  };

  // Cancel enrollment
  const cancelEnroll = () => {
    setConfirmDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!skillstree) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold">Skillstree Not Found</h1>
          <button 
            onClick={() => navigate(`/skillstree/${categoryId}`)}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded text-sm sm:text-base"
          >
            Back to Category
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 relative pb-24">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Breadcrumbs */}
        <div className="mb-4 sm:mb-6">
          <nav className="breadcrumbs-container">
            <img 
              src="/breadcrumb_icon.svg" 
              alt="Dashboard" 
              className="breadcrumb-icon"
            />
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate('/skillstree')}
              className="breadcrumb-link"
            >
              Core Categories
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate(`/skillstree/${categoryId}`)}
              className="breadcrumb-link"
            >
              {categoryTitle}
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text">Skillstree</span>
            {/* <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text breadcrumb-active">{skillstree.name}</span> */}
          </nav>
        </div>

        {/* Skillstree Header Section */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 mb-8 sm:mb-12">
          {/* Badge Box Card */}
          <div className="flex-shrink-0">
            <div className="skillstree-header-badge-box">
              <img 
                src={skillstree.image_url} 
                alt={skillstree.name}
                className="skillstree-badge-image"
              />
            </div>
          </div>

          {/* Description & Details */}
          <div className="skillstree-header-content">
            <p className="text-white text-base sm:text-lg mt-0 sm:mt-6 mb-4 sm:mb-6 text-left">{skillstree.description}</p>
            <div className="space-y-4">
              <div className="skillstree-detail-item">
                <div className="skillstree-detail-row">
                  <span className="skillstree-detail-icon">$</span>
                  <span className="skillstree-detail-label">Monthly compensation</span>
                  <span className="skillstree-detail-value">{skillstree.monthlyCompensation}</span>
                </div>
              </div>
              <div className="skillstree-detail-item">
                <div className="skillstree-detail-row">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="skillstree-detail-icon">
                    <rect x="2" y="4" width="12" height="10" rx="1" stroke="white" strokeWidth="1.5" fill="none"/>
                    <path d="M5 4V2C5 1.44772 5.44772 1 6 1H10C10.5523 1 11 1.44772 11 2V4" stroke="white" strokeWidth="1.5"/>
                    <circle cx="8" cy="9" r="1" fill="white"/>
                    <path d="M6 12H10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span className="skillstree-detail-label">
                    <strong>High Demand</strong> in{' '}
                    {skillstree.demand.replace(/^High demand\s+in\s+/i, '')}
                  </span>
                </div>
              </div>
              <div className="skillstree-enroll-button-container">
                <button
                  className="skillstree-enroll-button"
                  onClick={handleEnroll}
                  disabled={enrolling || skillstree.is_enrolled}
                >
                  {enrolling 
                    ? 'Enrolling...' 
                    : skillstree.is_enrolled 
                      ? 'Enrolled' 
                      : 'Enroll'
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Passport SVG - Only show when learning_progress is 100 */}
          {skillstree.learning_progress === 100 && (
            <div className="flex-shrink-0 flex flex-col items-center sm:ml-[8rem]">
              <img 
                src="/Passport.svg" 
                alt="Passport" 
                className="h-auto w-auto"
              />
              <Link 
                to="/achievements#passport"
                className="text-white text-sm sm:text-base mt-2 underline hover:text-blue-300 transition-colors text-center"
              >
                View My Passport
              </Link>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
          {/* Microskills Section */}
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-left">Microskills:</h2>
            <div className="microskills-list">
              {skillstree.microskills.map((microskill, index) => {
                const isFirstThree = index < 3;
                const isLast = index === skillstree.microskills.length - 1;
                
                return (
                  <div key={microskill.id || `microskill-${index}`} className="microskill-item">
                    {/* Circular Badge */}
                    <div className="microskill-badge-wrapper">
                      <div className={`microskill-circle ${isFirstThree ? 'microskill-circle-orange' : 'microskill-circle-green'}`}>
                        <div className="microskill-circle-content">
                          <div className="microskill-circle-text">
                            <div className="microskill-circle-code">{microskill.code}</div>
                            <div className="microskill-circle-name text-center">{microskill.name}</div>
                          </div>
                        </div>
                      </div>
                      {/* Vertical Connector */}
                      {!isLast && <div className="microskill-connector-vertical" />}
                    </div>

                    {/* Detail Card */}
                    <div className="microskill-detail-card">
                      {/* Stacked Images on Left */}
                      <div className="microskill-images-stack">
                        {microskill.videos && microskill.videos.length > 0
                          ? microskill.videos
                              .filter((video) => video.thumbnail?.url)
                              .slice(0, 4)
                              .map((video, imgIndex) => {
                                const thumbnailPath = video.thumbnail?.url || '';
                                return (
                                  <div key={imgIndex} className="microskill-thumbnail">
                                    <img 
                                      src={thumbnailPath}
                                      alt='thumbnail'
                                      className="microskill-thumbnail-image"
                                    />
                                  </div>
                                );
                              })
                          : null}
                      </div>
                      
                      <div className="microskill-detail-content">
                        <h3 className="microskill-detail-title text-left">{microskill.title}</h3>
                        <div className="microskill-detail-info">
                          <div className="microskill-info-item">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2 4H14V12H2V4Z" stroke="white" strokeWidth="1.5" fill="none"/>
                              <path d="M5 2V4M11 2V4" stroke="white" strokeWidth="1.5"/>
                              <path d="M2 7H14" stroke="white" strokeWidth="1.5"/>
                            </svg>
                            <span>{microskill.sessions} Sessions</span>
                          </div>
                          <div className="microskill-info-item">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.5" fill="none"/>
                              <path d="M8 4V8L10 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>{microskill.hours} Hours</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-center mx-auto sm:mx-0 sm:items-start">
                        <button 
                          className="microskill-view-detail-btn"
                          onClick={() => {
                            // Allow access if microskill is completed, even without subscription
                            const isCompleted = microskill.learning_status === 'completed' || microskill.learning_progress === 100;
                            
                            // Check if user doesn't have subscription or has free subscription and microskill is not free (unless completed)
                            if ((!hasSubscription || hasFreeSubscription) && microskill.is_free === false && !isCompleted) {
                              window.scrollTo(0, 0);
                              navigate('/profile?tab=subscription');
                              return;
                            }
                            
                            // Check if user is enrolled (explicitly check for false or undefined)
                            // Allow access if completed even if not enrolled
                            if (skillstree.is_enrolled !== true && !isCompleted) {
                              toast.info('Enroll to the Skillstree to view more details', {
                                position: "top-right",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                              });
                              return;
                            }
                            if (microskill.id) {
                              navigate(`/skillstree/${categoryId}/${skillstreeId}/${microskill.id}`);
                            }
                          }}
                          disabled={(() => {
                            // Allow access if microskill is completed
                            const isCompleted = microskill.learning_status === 'completed' || microskill.learning_progress === 100;
                            // Enable button if showing "Upgrade Now" (user can always click to upgrade)
                            if ((!hasSubscription || hasFreeSubscription) && microskill.is_free === false && !isCompleted) {
                              return false; // Allow click to redirect to subscription
                            }
                            // Allow if completed
                            if (isCompleted) {
                              return !microskill.id; // Only disable if no ID
                            }
                            // Otherwise disable if no ID or can't access or not enrolled
                            return (!microskill.id || !microskill.canAccess || skillstree.is_enrolled !== true);
                          })()}
                          style={{
                            opacity: (() => {
                              const isCompleted = microskill.learning_status === 'completed' || microskill.learning_progress === 100;
                              // Show as disabled if not enrolled and not completed (but not if showing upgrade button)
                              if (skillstree.is_enrolled !== true && !isCompleted && !((!hasSubscription || hasFreeSubscription) && microskill.is_free === false)) {
                                return 0.5;
                              }
                              return 1;
                            })(),
                            cursor: (() => {
                              const isCompleted = microskill.learning_status === 'completed' || microskill.learning_progress === 100;
                              // Show not-allowed if not enrolled and not completed (but not if showing upgrade button)
                              if (skillstree.is_enrolled !== true && !isCompleted && !((!hasSubscription || hasFreeSubscription) && microskill.is_free === false)) {
                                return 'not-allowed';
                              }
                              return 'pointer';
                            })()
                          }}
                        >
                          {(!hasSubscription || hasFreeSubscription) && microskill.is_free === false 
                            ? 'Upgrade Now' 
                            : (microskill.learning_status === 'completed' || microskill.learning_progress === 100)
                              ? 'Review Learning'
                              : 'View Detail'}
                        </button>
                        {(!hasSubscription || hasFreeSubscription) && microskill.is_free === false && (
                          <p className="text-xs text-gray-400 mt-2 text-center">
                            Only for SKILLSTURE PLUS user
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Related Skillstree Section */}
          <div className="w-full lg:w-80 flex-shrink-0 related-skillstree-section">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-left">Related Skillstree:</h2>
            <div className="related-skillstree-carousel-wrapper">
              <div className="related-skillstree-carousel" ref={emblaRef}>
                <div className="related-skillstree-carousel-container">
                  {relatedSkillstrees.length > 0 ? (
                    relatedSkillstrees.map((related) => (
                      <div key={related.id} className="related-skillstree-slide">
                        <div className="related-skillstree-card">
                          <div className="related-skillstree-image">
                            <img src={related.image_url || ''} alt={related.name} />
                            <div className="related-skillstree-image-overlay">
                              <span className="related-skillstree-image-title">{related.name}</span>
                            </div>
                          </div>
                          <div className="related-skillstree-content">
                            <p className="related-skillstree-description">
                              {related.description}
                            </p>
                            <button 
                              className="related-skillstree-select-btn"
                              onClick={() => {
                                navigate(`/skillstree/${categoryId}/${related.id}`);
                              }}
                            >
                              Select
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="related-skillstree-slide">
                      <div className="related-skillstree-card">
                        <div className="related-skillstree-content">
                          <p className="related-skillstree-description text-center">
                            No related skillstrees available
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Navigation Arrows */}
              <button className="related-skillstree-nav-arrow related-skillstree-nav-arrow-prev" onClick={scrollPrev}>
                ‹
              </button>
              <button className="related-skillstree-nav-arrow related-skillstree-nav-arrow-next" onClick={scrollNext}>
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-neutral-800 border-neutral-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Enrollment</DialogTitle>
            <DialogDescription className="text-neutral-300">
              Are you sure you want to enroll in this skillstree? This action will add it to your learning path.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={cancelEnroll}
              className="px-4 py-2 bg-neutral-600 hover:bg-neutral-500 text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmEnroll}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillstreeMicroskillPage;

